import { normalizeHeader, SYNONYMS, TARGET_FIELDS, NAME_PART_KEYS, ADDRESS_PART_KEYS } from './fieldSynonyms';

const ALL_KEYS = [...TARGET_FIELDS.map((f) => f.key), ...NAME_PART_KEYS];

function bigrams(s) {
  const out = [];
  for (let i = 0; i < s.length - 1; i += 1) out.push(s.slice(i, i + 2));
  return out;
}

export function diceCoefficient(a, b) {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const aBg = bigrams(a);
  const bBg = bigrams(b);
  const bCounts = {};
  bBg.forEach((g) => { bCounts[g] = (bCounts[g] || 0) + 1; });
  let hits = 0;
  aBg.forEach((g) => { if (bCounts[g] > 0) { bCounts[g] -= 1; hits += 1; } });
  return (2 * hits) / (aBg.length + bBg.length);
}

// Score one normalized header against one field key: exact=1, substring=0.8, else fuzzy.
function scoreField(norm, key) {
  const syns = SYNONYMS[key] || [];
  let best = 0;
  for (const s of syns) {
    if (norm === s) return 1;
    if (norm.includes(s) || s.includes(norm)) best = Math.max(best, 0.8);
    else best = Math.max(best, diceCoefficient(norm, s));
  }
  return best;
}

function toConfidence(score) {
  if (score >= 1) return 'high';
  if (score >= 0.8) return 'medium';
  return 'low';
}

export function matchColumns(headers) {
  const THRESHOLD = 0.6;
  const norms = headers.map((h) => normalizeHeader(h));

  // Build all (sourceIndex, key, score) candidates above threshold.
  const candidates = [];
  norms.forEach((norm, sourceIndex) => {
    if (!norm) return;
    ALL_KEYS.forEach((key) => {
      const score = scoreField(norm, key);
      if (score >= THRESHOLD) candidates.push({ sourceIndex, key, score });
    });
  });

  // Greedy assignment: highest score first; one source per key, one key per source.
  candidates.sort((a, b) => (b.score - a.score) || (a.sourceIndex - b.sourceIndex));
  const columnMap = Object.fromEntries(ALL_KEYS.map((k) => [k, null]));
  const confidence = {};
  const usedSources = new Set();
  candidates.forEach(({ sourceIndex, key, score }) => {
    if (columnMap[key] !== null || usedSources.has(sourceIndex)) return;
    columnMap[key] = sourceIndex;
    confidence[key] = toConfidence(score);
    usedSources.add(sourceIndex);
  });

  // If an explicit patient_name column was found, prefer it and free the name parts.
  if (columnMap.patient_name !== null) {
    NAME_PART_KEYS.forEach((k) => {
      if (columnMap[k] !== null) { usedSources.delete(columnMap[k]); columnMap[k] = null; delete confidence[k]; }
    });
  }

  const ignored = [];
  headers.forEach((_, i) => { if (!usedSources.has(i)) ignored.push(i); });

  return { columnMap, confidence, ignored };
}

// Best-effort mapping of the file's address columns to canonical address parts,
// used to pre-suggest a combined "address" column. Exact/substring match on the
// normalized header; first column to claim a part wins.
export function detectAddressParts(headers) {
  const claimed = {};
  (headers || []).forEach((h, i) => {
    const norm = normalizeHeader(h);
    if (!norm) return;
    for (const key of ADDRESS_PART_KEYS) {
      if (claimed[key] != null) continue;
      const syns = SYNONYMS[key] || [];
      if (syns.some((s) => norm === s || norm.includes(s) || s.includes(norm))) { claimed[key] = i; break; }
    }
  });
  // Emit the parts in canonical ADDRESS_PART_KEYS order so a combined address folds
  // them sensibly (street, apt, city, state, zip) regardless of the file's column order.
  const out = {};
  ADDRESS_PART_KEYS.forEach((key) => { if (claimed[key] != null) out[key] = claimed[key]; });
  return out;
}
